import { Button, TableRow, Table, TableContainer, TableHead, TableCell, TableBody, Typography, Box, Paper, IconButton, Menu, MenuItem } from "@mui/material";
import ClientProvider from "../../../modules/ClientProvider";
import { useAuth0 } from "@auth0/auth0-react";
import { useState } from "react";
import { useSmartleadCampaigns } from "../hooks/useSmartleadCampaigns";
import dayjs from "dayjs";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { CornerDownRight, Download, Mail, ChevronRight, ChevronDown } from "lucide-react";

export const AdminSmartleadCampaigns = () => {

    const { getAccessTokenSilently } = useAuth0();
    const adminClient = ClientProvider.provideAdminClient();
    const [loadingStates, setLoadingStates] = useState({
        campaigns: false,
        sequences: {} as Record<string, boolean>,
        activities: {} as Record<string, boolean>,
        tags: {} as Record<string, boolean>
    });
    const [expandedProjects, setExpandedProjects] = useState<Record<string, boolean>>({});
    const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; campaignUuid: string } | null>(null);
    const [refreshMenuAnchor, setRefreshMenuAnchor] = useState<HTMLElement | null>(null);

    const { campaigns, reloadCampaigns } = useSmartleadCampaigns();

    const loadCampaigns = async () => {
        setLoadingStates(prev => ({ ...prev, campaigns: true }));
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await adminClient.loadCampaigns(accessToken);
        if (!error) {
            reloadCampaigns();
        }
        setLoadingStates(prev => ({ ...prev, campaigns: false }));
    }

    const loadLeads = async (campaignUuid: string) => {
        setLoadingStates(prev => ({
            ...prev,
            sequences: { ...prev.sequences, [campaignUuid]: true }
        }));
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await adminClient.loadLeads(campaignUuid, accessToken);
        if (!error) {
            reloadCampaigns();
        } else {
            console.error(error);
        }
        setLoadingStates(prev => ({
            ...prev,
            sequences: { ...prev.sequences, [campaignUuid]: false }
        }));
    }

    const loadActivities = async (campaignUuid: string) => {
        setLoadingStates(prev => ({
            ...prev,
            activities: { ...prev.activities, [campaignUuid]: true }
        }));
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await adminClient.loadActivities(campaignUuid, accessToken);
        if (!error) {
            reloadCampaigns();
        }
        setLoadingStates(prev => ({
            ...prev,
            activities: { ...prev.activities, [campaignUuid]: false }
        }));
    }

    const loadTags = async (campaignUuid: string) => {
        setLoadingStates(prev => ({
            ...prev,
            tags: { ...prev.tags, [campaignUuid]: true }
        }));
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await adminClient.loadTags(campaignUuid, accessToken);
        if (!error) {
            reloadCampaigns();
        }
        setLoadingStates(prev => ({
            ...prev,
            tags: { ...prev.tags, [campaignUuid]: false }
        }));
    }

    const reloadActiveCampaigns = async () => {
        setLoadingStates(prev => ({
            ...prev,
            campaigns: true
        }));
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await adminClient.reloadActiveCampaigns(accessToken);
        if (!error) {
            reloadCampaigns();
        }
        setLoadingStates(prev => ({
            ...prev,
            campaigns: false
        }));
    }

    const downloadActivities = async () => {
        // Define CSV headers
        const headers = [
            "Company",
            "Campaign",
            "Sequence Number",
            "Lead Name",
            "Lead Email",
            "Sent At",
            "Opened At",
            "Clicked At",
            "Replied At",
            "Open Count",
            "Click Count",
            "Is Unsubscribed",
            "Is Bounced",
            "Campaign Tag",
            "Channel Tag",
            "Type Tag",
            "Tags",
            "Acquired At",
            "Activated At",
        ];

        const accessToken = await getAccessTokenSilently();
        const { data, error } = await adminClient.fetchActivities(accessToken);

        if (!error) {
            //console.log(data);
        } else {
            console.error(error);
            return;
        }

        // Map data to CSV rows
        const csvRows = data.activities?.map((activity: any) => [
            activity.campaign?.project?.name,
            activity.campaign?.name,
            activity.sequence_number,
            activity.campaign_lead?.contact?.first_name + " " + activity.campaign_lead?.contact?.last_name,
            activity.campaign_lead?.contact?.email_address,
            activity.sent_at,
            activity.opened_at,
            activity.clicked_at,
            activity.replied_at,
            activity.open_count,
            activity.click_count,
            activity.is_unsubscribed,
            activity.is_bounced,
            activity.campaign.campaign_tag,
            activity.campaign.channel_tag,
            activity.campaign.type_tag,
            activity.campaign.tags,
            activity.campaign_lead?.advisor?.created_at,
            activity.campaign_lead?.advisor?.completed_at,
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...csvRows.map((row: any) => row.map((cell: any) => `"${cell || ''}"`).join(','))
        ].join('\n');


        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `smartlead-campaign-activities.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Group campaigns by project
    const campaignsByProject = campaigns.reduce((acc, campaign) => {
        const projectName = campaign.project?.name || 'Unassigned';
        if (!acc[projectName]) {
            acc[projectName] = [];
        }
        acc[projectName].push(campaign);
        return acc;
    }, {} as Record<string, typeof campaigns>);

    // Organize campaigns into parent-child structure
    const organizeCampaigns = (projectCampaigns: typeof campaigns) => {
        const parentCampaigns = projectCampaigns.filter(campaign => !campaign.parent_campaign);
        const childCampaigns = projectCampaigns.filter(campaign => campaign.parent_campaign);

        // Create a map of parent campaigns with their children
        const organizedCampaigns = parentCampaigns.map(parent => ({
            ...parent,
            children: childCampaigns.filter(child => child.parent_campaign?.id === parent.id)
        }));

        // Add any orphaned child campaigns (in case parent is not in the list)
        const orphanedChildren = childCampaigns.filter(child =>
            !parentCampaigns.some(parent => parent.id === child.parent_campaign?.id)
        );

        return [...organizedCampaigns, ...orphanedChildren];
    };

    // Calculate rollup stats for each project
    const projectStats = (Object.entries(campaignsByProject) as [string, typeof campaigns][]).reduce((acc, [projectName, projectCampaigns]) => {
        acc[projectName] = {
            total: projectCampaigns.reduce((sum: number, campaign: typeof campaigns[0]) => sum + (campaign.parent_campaign ? 0 : campaign.leads_total || 0), 0),
            pending: projectCampaigns.reduce((sum: number, campaign: typeof campaigns[0]) => sum + (campaign.parent_campaign ? 0 : campaign.leads_started || 0), 0),
            inProgress: projectCampaigns.reduce((sum: number, campaign: typeof campaigns[0]) => sum + (campaign.parent_campaign ? 0 : campaign.leads_inprogress || 0), 0),
            paused: projectCampaigns.reduce((sum: number, campaign: typeof campaigns[0]) => sum + (campaign.parent_campaign ? 0 : campaign.leads_paused || 0), 0),
            completed: projectCampaigns.reduce((sum: number, campaign: typeof campaigns[0]) => sum + (campaign.parent_campaign ? 0 : campaign.leads_completed || 0), 0),
        };
        return acc;
    }, {} as Record<string, { total: number; pending: number; inProgress: number; paused: number; completed: number; }>);

    // Sort project names alphabetically
    const sortedProjectNames = Object.keys(campaignsByProject).sort();

    const toggleProject = (projectName: string) => {
        setExpandedProjects(prev => ({
            ...prev,
            [projectName]: !prev[projectName]
        }));
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, campaignUuid: string) => {
        setMenuAnchor({ element: event.currentTarget, campaignUuid });
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleAction = async (action: 'tags' | 'leads' | 'activities') => {
        if (!menuAnchor) return;

        const { campaignUuid } = menuAnchor;
        switch (action) {
            case 'tags':
                await loadTags(campaignUuid);
                break;
            case 'leads':
                await loadLeads(campaignUuid);
                break;
            case 'activities':
                await loadActivities(campaignUuid);
                break;
        }
        handleMenuClose();
    };


    const renderCampaignRow = (campaign: typeof campaigns[0], isChild: boolean = false) => {
        const smartleadUrl = campaign.parent_campaign ? `https://app.smartlead.ai/app/campaign/${campaign.parent_campaign?.smartlead_id}/subsequence/${campaign.smartlead_id}/analytics`
            : `https://app.smartlead.ai/app/email-campaign/${campaign.smartlead_id}/analytics`;
        return (
            <TableRow key={campaign.id}>
                <TableCell><Mail size={16} /></TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isChild && (
                            <CornerDownRight size={16} style={{ marginRight: 8 }} />
                        )}
                        <strong><a href={smartleadUrl} target="_blank" rel="noopener noreferrer">{campaign.name}</a></strong>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', paddingLeft: isChild ? "24px" : 0 }}>{campaign.status}</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{campaign.leads_total ?? "---"}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Total Leads</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{campaign.leads_started ?? "---"}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Remaining</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{campaign.leads_inprogress ?? "---"}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>In Progress</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{campaign.leads_paused ?? "---"}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Paused</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{campaign.leads_completed ?? "---"}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Completed</Typography>
                </TableCell>
                <TableCell>
                    <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, campaign.uuid)}
                        disabled={loadingStates.tags[campaign.uuid] ||
                            loadingStates.sequences[campaign.uuid] ||
                            loadingStates.activities[campaign.uuid]}
                    >
                        <MoreHorizIcon />
                    </IconButton>
                </TableCell>
            </TableRow>
        );
    }

    return <div>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Campaigns</h3>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                    <Button variant="contained" color="primary" onClick={downloadActivities} disabled={loadingStates.campaigns}>
                        <Download size={16} style={{ marginRight: 8 }} /> Download Activities
                    </Button>
                    <IconButton
                        size="small"
                        onClick={(e) => setRefreshMenuAnchor(e.currentTarget)}
                        disabled={loadingStates.campaigns}
                    >
                        <MoreHorizIcon />
                    </IconButton>
                </Box>
            </Box>
        </Box>
        <TableContainer sx={{ width: '100%' }}>
            <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow>
                        <TableCell width="25px"></TableCell>
                        <TableCell width="300px"></TableCell>
                        <TableCell width="92px"></TableCell>
                        <TableCell width="92px"></TableCell>
                        <TableCell width="92px"></TableCell>
                        <TableCell width="92px"></TableCell>
                        <TableCell width="92px"></TableCell>
                        <TableCell width="92px"></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedProjectNames.map((projectName) => (
                        <>
                            <TableRow key={`header-${projectName}`}>
                                <TableCell sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                                    <IconButton
                                        onClick={() => toggleProject(projectName)}
                                        sx={{ padding: 0 }}
                                    >
                                        {expandedProjects[projectName] ? <ChevronDown size={16} strokeWidth={2.5} /> : <ChevronRight size={16} strokeWidth={2.5} />}
                                    </IconButton>
                                </TableCell>
                                <TableCell colSpan={1} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        {projectName}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{projectStats[projectName].total ?? "---"}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Total Leads</Typography>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{projectStats[projectName].pending ?? "---"}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Remaining</Typography>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{projectStats[projectName].inProgress ?? "---"}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>In Progress</Typography>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{projectStats[projectName].paused ?? "---"}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Paused</Typography>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{projectStats[projectName].completed ?? "---"}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Completed</Typography>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}></TableCell>
                            </TableRow>
                            {expandedProjects[projectName] && organizeCampaigns(campaignsByProject[projectName]).map((campaign: any) => (
                                <>
                                    {renderCampaignRow(campaign)}
                                    {campaign.children?.map((child: typeof campaigns[0]) => renderCampaignRow(child, true))}
                                </>
                            ))}
                        </>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
        <Menu
            anchorEl={menuAnchor?.element}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
        >
            <MenuItem
                onClick={() => handleAction('tags')}
                disabled={loadingStates.tags[menuAnchor?.campaignUuid || '']}
            >
                {loadingStates.tags[menuAnchor?.campaignUuid || ''] ? "Loading..." : "Load Tags"}
            </MenuItem>
            <MenuItem
                onClick={() => handleAction('leads')}
                disabled={loadingStates.sequences[menuAnchor?.campaignUuid || '']}
            >
                {loadingStates.sequences[menuAnchor?.campaignUuid || ''] ? "Loading..." : "Load Leads"}
            </MenuItem>
            <MenuItem
                onClick={() => handleAction('activities')}
                disabled={loadingStates.activities[menuAnchor?.campaignUuid || '']}
            >
                {loadingStates.activities[menuAnchor?.campaignUuid || ''] ? "Loading..." : "Load Activities"}
            </MenuItem>
        </Menu>
        <Menu
            anchorEl={refreshMenuAnchor}
            open={Boolean(refreshMenuAnchor)}
            onClose={() => setRefreshMenuAnchor(null)}
        >
            <MenuItem
                onClick={() => {
                    loadCampaigns();
                    setRefreshMenuAnchor(null);
                }}
                disabled={loadingStates.campaigns}
            >
                {loadingStates.campaigns ? "Loading..." : "Load Campaigns"}
            </MenuItem>
            <MenuItem
                onClick={() => {
                    reloadActiveCampaigns();
                    setRefreshMenuAnchor(null);
                }}
                disabled={loadingStates.campaigns}
            >
                {loadingStates.campaigns ? "Loading..." : "Reload Active Campaigns"}
            </MenuItem>
        </Menu>
    </div>;
}